'use client'

import * as React from 'react'
import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { XIcon } from 'lucide-react'

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        // Ink-based scrim (brand-token-migration T8) — color-mix against --ink-900
        // rather than a raw black/10, per untitledui-reference.md's Shadow section.
        'fixed inset-0 z-50 bg-[color-mix(in_srgb,var(--ink-900)_10%,transparent)] transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs',
        className
      )}
      {...props}
    />
  )
}

/**
 * Side-sheet width, by Tailwind's `max-w` scale, applied to the left/right sides only (top/bottom
 * sheets are full-width). A variant rather than a caller-side `className="sm:max-w-*"` because the
 * base classes below set the width under a `data-[side=…]:` prefix, and a plain `sm:max-w-*` from
 * outside never wins against it — found in the browser on 2026-09-10 (R3.5): a drawer (the
 * since-retired pinned-keywords one) asked for `sm:max-w-2xl` and rendered at `sm` (384px). Ten other callers carry the same
 * dead override today; migrate them to `size` rather than adding an eleventh class.
 */
export type SheetSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const SHEET_SIZE_CLASS: Record<SheetSize, string> = {
  sm: 'data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm',
  md: 'data-[side=left]:sm:max-w-md data-[side=right]:sm:max-w-md',
  lg: 'data-[side=left]:sm:max-w-lg data-[side=right]:sm:max-w-lg',
  xl: 'data-[side=left]:sm:max-w-xl data-[side=right]:sm:max-w-xl',
  '2xl': 'data-[side=left]:sm:max-w-2xl data-[side=right]:sm:max-w-2xl',
}

function SheetContent({
  className,
  children,
  side = 'right',
  size = 'sm',
  showCloseButton = true,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Width on the `sm:` breakpoint and up for left/right sheets. Default `sm` (384px), the value
   * every sheet rendered at before this prop existed. */
  size?: SheetSize
  showCloseButton?: boolean
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          // Codex §3: floating layer — shadow-overlay + hairline border; dark theme follows --popover token.
          // No radius here (deliberate) — a side sheet is flush to the viewport edge it slides in from.
          // round-4 audit: duration-[var(--dur-panel)] (220ms, the source's own
          // literal "220ms" for this exact slide transition) — was duration-200.
          // overflow-y-auto: content taller than the viewport was clipped with no way to
          // reach it — the panel itself is h-full/fixed with no scroll region of its own,
          // so long drawer content (SourceDrawer, prompt detail, …) rendered past the
          // bottom of the screen. The close button stays anchored (it's absolutely
          // positioned against this same fixed box, not the scrolled content).
          'fixed z-50 flex flex-col gap-4 overflow-y-auto bg-popover bg-clip-padding text-sm text-popover-foreground shadow-overlay ring-1 ring-border transition duration-[var(--dur-panel)] ease-in-out data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem]',
          SHEET_SIZE_CLASS[size],
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            // round-4 audit: top-3.5/right-3.5 (14px, matches the source's
            // literal close-button offset) — was top-3/right-3 (12px).
            // z-20: SheetHeader is `sticky top-0 z-10` with an opaque fill, so a
            // close button with no z-index of its own painted underneath it.
            render={
              <Button variant="ghost" className="absolute top-3.5 right-3.5 z-20" size="icon-sm" />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  // round-4 audit: border-b per the source's literal header specimen — was
  // entirely unstyled beneath the header.
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        'sticky top-0 z-10 flex flex-col gap-0.5 border-b border-border bg-popover p-4',
        className
      )}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  // round-4 audit: paper-50 + border-t per the source's literal drawer
  // footer specimen ("background: var(--paper-50); border-top: 1px solid
  // var(--border); padding: 14px 20px") — this footer had ZERO styling
  // before (no background, no border at all), worse than Dialog's footer,
  // which at least had the wrong-toned one. Painted through the
  // `--background` role (paper-50 in light): raw `bg-paper-50` has no dark
  // value and left a near-white footer on a dark sheet.
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        'mt-auto flex flex-col gap-2 border-t border-border bg-background px-5 py-3.5',
        className
      )}
      {...props}
    />
  )
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-heading text-base font-medium text-foreground', className)}
      {...props}
    />
  )
}

function SheetDescription({ className, ...props }: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
